import PropTypes from "prop-types";
import clsx from "clsx";
import { Fragment, useMemo, useState } from "react";
import { Fab, Grid, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";

// Icons
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

function MenuTitle(props) {
	const { title = "", isSubText = false } = props;
	return (
		<Typography
			variant="subtitle2"
			width={"200px"}
			noWrap
			component="div"
			className={clsx({
				"!font-montserrat text-default !text-[14px]": !isSubText,
				"!font-sans text-neutral-gray !text-[14.5px]": isSubText,
			})}
		>
			{title}
		</Typography>
	);
}
MenuTitle.propTypes = {
	title: PropTypes.string,
	isSubText: PropTypes.bool,
};

export default function AddSection(props) {
	const {
		title = "Add Section",
		sections = new Map(),
		sectionsInUse = new Map(),
		disabled = false,
		readOnly = false,
		onActionAdd = () => {},
	} = props;

	// States
	const [anchorEl, setAnchorEl] = useState(null);
	const [open, setOpen] = useState(false);

	const handleClick = (event) => {
		anchorEl ? setAnchorEl(null) : setAnchorEl(event.currentTarget);
		setOpen(!open);
	};

	const handleClose = () => {
		setAnchorEl(null);
		setOpen(false);
	};

	const handleMenuClick = (sectionKey) => {
		setAnchorEl(null);
		setOpen(false);
		onActionAdd?.(sectionKey);
	};

	const useSections = useMemo(() => {
		const result = new Map();
		for (const [key, value] of sections) {
			if (!sectionsInUse.has(key)) {
				result.set(key, value);
			}
		}
		return result;
	}, [sections, sectionsInUse]);

	const isDisabled = Boolean(!useSections.size || disabled);

	return (
		<Fragment>
			<Fab
				variant="extended"
				size="small"
				aria-label="add"
				className={clsx({
					"!fixed bottom-[25px] right-[200px] !text-white !normal-case !h-[40px] !min-w-[178px] !rounded-full z-[9999]": true,
					"!bg-primary hover:!shadow-primary-shadow-50": !isDisabled,
					"!bg-disabled": isDisabled,
				})}
				onClick={handleClick}
				disabled={isDisabled}
			>
				<Typography className="!font-montserrat !text-[16px]" sx={{ ml: 1 }}>
					{title}
				</Typography>
				<AddCircleRounded className="!text-[22px]" fontSize={"inherit"} sx={{ ml: "15px", mr: 0.5 }} />
			</Fab>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				sx={{
					marginTop: "-13px",
				}}
				slotProps={{
					paper: {
						elevation: 0,
						sx: {
							overflow: "visible",
							filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
							borderRadius: "10px",
							mb: 1.5,
							"&:after": {
								content: '""',
								display: "block",
								position: "relative",
								right: 25,
								width: 10,
								height: 10,
								marginLeft: "auto",
								bgcolor: "background.paper",
								transform: "translateY(50%) rotate(45deg)",
								zIndex: 0,
							},
						},
					},
				}}
				transformOrigin={{ horizontal: "right", vertical: "bottom" }}
				anchorOrigin={{ horizontal: "right", vertical: "top" }}
			>
				<IconButton onClick={handleClose} aria-label="close" size="small" className="main-add-section-close-btn">
					<CloseRoundedIcon fontSize="inherit" />
				</IconButton>
				<Grid
					container
					mt={"30px"}
					sx={{ marginX: "40px" }}
					justifyContent="space-between"
					width={useSections.size <= 1 ? "200px" : "440px"}
				>
					{Array.from(useSections).map(([section, information]) => (
						<Tooltip
							key={section}
							title={information?.disabled ? information?.tooltipDisabled || "" : ""}
							placement="top"
							arrow
							PopperProps={{
								disablePortal: true,
							}}
							className="regular-pt-sans-pro-narrow-label"
						>
							<Grid item width="200px" mb={"20px"} key={section}>
								<MenuItem
									disabled={readOnly || Boolean(information.disabled)}
									onClick={() => {
										handleMenuClick(section);
									}}
									className="section-add-item-btn"
								>
									<Grid
										container
										direction="column"
										className="[&>*:first-child]:hover:underline [&>*:first-child]:hover:underline-offset-[2px]"
									>
										<MenuTitle title={information?.shortTitle ?? information?.title} />
										<MenuTitle title={information?.description} isSubText={true} />
									</Grid>
								</MenuItem>
							</Grid>
						</Tooltip>
					))}
				</Grid>
			</Menu>
		</Fragment>
	);
}
AddSection.propTypes = {
	title: PropTypes.string,
	sections: PropTypes.object,
	sectionsInUse: PropTypes.object,
	disabled: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionAdd: PropTypes.func,
};
